import {
  $LOADER,
  $PAGE,
  createLoader,
  GetLoaderData,
  MetaFunc,
} from "~/util/Loaders";
import { useEffect, useRef, useState } from "react";
import { MetaArgs } from "react-router";

type GemType = "red" | "blue" | "green" | "yellow" | "purple";

interface Gem {
  type: GemType;
  destroyed: boolean;
}

const CELL_SIZE = 60;
const BOARD_WIDTH = 8;
const BOARD_HEIGHT = 8;

const GEM_TYPES: GemType[] = ["red", "blue", "green", "yellow", "purple"];
const COLORS: Record<GemType, string> = {
  red: "#e74c3c",
  blue: "#3498db",
  green: "#2ecc71",
  yellow: "#f1c40f",
  purple: "#9b59b6",
};

function getRandomGem(): GemType {
  return GEM_TYPES[Math.floor(Math.random() * GEM_TYPES.length)];
}

function createBoard(): Gem[][] {
  const board: Gem[][] = [];
  for (let y = 0; y < BOARD_HEIGHT; y++) {
    board[y] = [];
    for (let x = 0; x < BOARD_WIDTH; x++) {
      board[y][x] = { type: getRandomGem(), destroyed: false };
    }
  }
  return board;
}

function findMatches(board: Gem[][]): Set<string> {
  const matches = new Set<string>();

  // По строкам
  for (let y = 0; y < BOARD_HEIGHT; y++) {
    let currentType = null;
    let streak = 0;
    for (let x = 0; x < BOARD_WIDTH; x++) {
      if (!board[y][x].destroyed && board[y][x].type === currentType) {
        streak++;
      } else {
        if (streak >= 3) {
          for (let i = x - streak; i < x; i++) {
            matches.add(`${y},${i}`);
          }
        }
        currentType = board[y][x].destroyed ? null : board[y][x].type;
        streak = 1;
      }
    }
    if (streak >= 3) {
      for (let i = BOARD_WIDTH - streak; i < BOARD_WIDTH; i++) {
        matches.add(`${y},${i}`);
      }
    }
  }

  // По столбцам
  for (let x = 0; x < BOARD_WIDTH; x++) {
    let currentType = null;
    let streak = 0;
    for (let y = 0; y < BOARD_HEIGHT; y++) {
      if (!board[y][x].destroyed && board[y][x].type === currentType) {
        streak++;
      } else {
        if (streak >= 3) {
          for (let i = y - streak; i < y; i++) {
            matches.add(`${i},${x}`);
          }
        }
        currentType = board[y][x].destroyed ? null : board[y][x].type;
        streak = 1;
      }
    }
    if (streak >= 3) {
      for (let i = BOARD_HEIGHT - streak; i < BOARD_HEIGHT; i++) {
        matches.add(`${i},${x}`);
      }
    }
  }

  return matches;
}

function removeMatches(board: Gem[][], matches: Set<string>): void {
  for (const pos of matches) {
    const [y, x] = pos.split(",").map(Number);
    board[y][x].destroyed = true;
  }
}

function collapseBoard(board: Gem[][]): void {
  for (let x = 0; x < BOARD_WIDTH; x++) {
    let writeY = BOARD_HEIGHT - 1;
    for (let y = BOARD_HEIGHT - 1; y >= 0; y--) {
      if (!board[y][x].destroyed) {
        if (writeY !== y) {
          board[writeY][x] = board[y][x];
          board[y][x] = { type: getRandomGem(), destroyed: false };
        }
        writeY--;
      }
    }

    while (writeY >= 0) {
      board[writeY][x] = { type: getRandomGem(), destroyed: false };
      writeY--;
    }
  }
}

// --- Компонент игры ---
export const PuzzleGame: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number | null>(null);
  const [board, setBoard] = useState<Gem[][]>(createBoard());
  const [selected, setSelected] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [dragging, setDragging] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [swapAnimation, setSwapAnimation] = useState<{
    from: { x: number; y: number };
    to: { x: number; y: number };
    startTime: number;
    duration: number;
  } | null>(null);

  const [fadeOutAnimations, setFadeOutAnimations] = useState<Set<string>>(
    new Set(),
  );

  const drawBoard = (
    board: Gem[][],
    alpha = 1,
    swapAnim?: typeof swapAnimation,
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = CELL_SIZE * BOARD_WIDTH;
    canvas.height = CELL_SIZE * BOARD_HEIGHT;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let y = 0; y < BOARD_HEIGHT; y++) {
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const gem = board[y][x];

        if (gem.destroyed && !fadeOutAnimations.has(`${y},${x}`)) continue;

        ctx.globalAlpha = fadeOutAnimations.has(`${y},${x}`) ? alpha : 1;

        ctx.fillStyle = COLORS[gem.type];
        ctx.fillRect(
          x * CELL_SIZE,
          y * CELL_SIZE,
          CELL_SIZE - 2,
          CELL_SIZE - 2,
        );
        ctx.strokeRect(
          x * CELL_SIZE,
          y * CELL_SIZE,
          CELL_SIZE - 2,
          CELL_SIZE - 2,
        );
      }
    }

    ctx.globalAlpha = 1;

    // Рендер анимации свопа
    if (swapAnim) {
      const now = Date.now();
      const elapsed = now - swapAnim.startTime;
      const t = Math.min(elapsed / swapAnim.duration, 1);
      const easedT = easeInOutCubic(t);

      const x1 = lerp(
        swapAnim.from.x * CELL_SIZE,
        swapAnim.to.x * CELL_SIZE,
        easedT,
      );
      const y1 = lerp(
        swapAnim.from.y * CELL_SIZE,
        swapAnim.to.y * CELL_SIZE,
        easedT,
      );
      const x2 = lerp(
        swapAnim.to.x * CELL_SIZE,
        swapAnim.from.x * CELL_SIZE,
        easedT,
      );
      const y2 = lerp(
        swapAnim.to.y * CELL_SIZE,
        swapAnim.from.y * CELL_SIZE,
        easedT,
      );

      const gem1 = board[swapAnim.from.y][swapAnim.from.x];
      const gem2 = board[swapAnim.to.y][swapAnim.to.x];

      ctx.fillStyle = COLORS[gem1.type];
      ctx.fillRect(x1, y1, CELL_SIZE - 2, CELL_SIZE - 2);
      ctx.strokeRect(x1, y1, CELL_SIZE - 2, CELL_SIZE - 2);

      ctx.fillStyle = COLORS[gem2.type];
      ctx.fillRect(x2, y2, CELL_SIZE - 2, CELL_SIZE - 2);
      ctx.strokeRect(x2, y2, CELL_SIZE - 2, CELL_SIZE - 2);
    }

    // Выделение выбранного гема
    if (selected) {
      ctx.strokeStyle = "#ffff00";
      ctx.lineWidth = 4;
      ctx.strokeRect(
        selected.x * CELL_SIZE + 2,
        selected.y * CELL_SIZE + 2,
        CELL_SIZE - 6,
        CELL_SIZE - 6,
      );
    }
  };

  const lerp = (a: number, b: number, t: number) => a * (1 - t) + b * t;

  const animateSwap = (
    from: { x: number; y: number },
    to: { x: number; y: number },
  ) => {
    const anim = {
      from,
      to,
      startTime: Date.now(),
      duration: 200,
    };
    setSwapAnimation(anim);

    const animate = () => {
      drawBoard(board, 1, anim);
      const elapsed = Date.now() - anim.startTime;
      if (elapsed < anim.duration) {
        animationFrameId.current = requestAnimationFrame(animate);
      } else {
        setSwapAnimation(null);
        drawBoard(board);
      }
    };
    animationFrameId.current = requestAnimationFrame(animate);
  };

  const fadeOutGems = (board: Gem[][], matches: Set<string>) => {
    if (matches.size === 0) return;

    const animationDuration = 300;
    const start = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = now - start;
      const progress = Math.min(elapsed / animationDuration, 1);
      const alpha = 1 - easeOutCubic(progress);

      setFadeOutAnimations(new Set(matches));

      drawBoard(board, alpha);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        removeMatches(board, matches);
        collapseBoard(board);
        setFadeOutAnimations(new Set());

        const newMatches = findMatches(board);
        setTimeout(() => {
          setBoard([...board.map((row) => [...row])]);
          if (newMatches.size > 0) {
            fadeOutGems(board, newMatches);
          }
        }, 100);
      }
    };

    animate();
  };

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / CELL_SIZE);
    const y = Math.floor((e.clientY - rect.top) / CELL_SIZE);

    if (selected) {
      const dx = Math.abs(selected.x - x);
      const dy = Math.abs(selected.y - y);
      if ((dx === 1 && dy === 0) || (dx === 0 && dy === 1)) {
        const newBoard = board.map((row) => row.map((gem) => ({ ...gem })));
        animateSwap(selected, { x, y });
        swap(newBoard, selected, { x, y });

        const matches = findMatches(newBoard);
        if (matches.size > 0) {
          setTimeout(() => {
            fadeOutGems(newBoard, matches);
          }, 200);
        } else {
          setTimeout(() => {
            swap(newBoard, selected, { x, y });
            setBoard([...newBoard.map((row) => [...row])]);
          }, 200);
        }
      }
      setSelected(null);
      setDragging(null);
    } else {
      setSelected({ x, y });
      setDragging({ x, y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!dragging) return;

    const rect = canvasRef.current!.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / CELL_SIZE);
    const y = Math.floor((e.clientY - rect.top) / CELL_SIZE);

    const dx = Math.abs(dragging.x - x);
    const dy = Math.abs(dragging.y - y);

    if ((dx === 1 && dy === 0) || (dx === 0 && dy === 1)) {
      const newBoard = board.map((row) => row.map((gem) => ({ ...gem })));
      animateSwap(dragging, { x, y });
      swap(newBoard, dragging, { x, y });

      const matches = findMatches(newBoard);
      if (matches.size > 0) {
        setTimeout(() => {
          fadeOutGems(newBoard, matches);
        }, 200);
      } else {
        setTimeout(() => {
          swap(newBoard, dragging!, { x, y });
          setBoard([...newBoard.map((row) => [...row])]);
        }, 200);
      }

      setSelected(null);
      setDragging(null);
    }
  };

  const handleMouseUp = () => {
    setDragging(null);
  };

  const swap = (
    board: Gem[][],
    pos1: { x: number; y: number },
    pos2: { x: number; y: number },
  ) => {
    const temp = board[pos1.y][pos1.x];
    board[pos1.y][pos1.x] = board[pos2.y][pos2.x];
    board[pos2.y][pos2.x] = temp;
  };

  const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  };

  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  useEffect(() => {
    drawBoard(board);
  }, [board]);

  return (
    <div>
      <h2>Puzzle Quest (React + TypeScript)</h2>
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        onMouseDown={handleClick}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{
          border: "1px solid #000",
          cursor: dragging ? "grabbing" : "pointer",
        }}
      />
    </div>
  );
};

export const meta = (_args: MetaArgs) => {
  return {
    title: "Online Game",
  };
};

$PAGE({
  path: "game",

  loader: $LOADER?.(async (cx) => {
    return {};
  }),

  meta: (args) => {
    return {
      title: "Online Game",
    };
  },

  render: ({ data }) => {
    return (
      <div>
        <PuzzleGame />
      </div>
    );
  },
});
